import React, { useEffect, useState } from 'react';

interface SessionData {
  sessionId: string;
  merchantName: string;
  amount: number;
  currency: string;
  orderId?: string;
  status: string;
  expiresAt: string;
}

const MobilePayPage: React.FC = () => {
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

  // Parse session ID from URL path: /mobile-pay/:sessionId
  const pathParts = window.location.pathname.split('/');
  const sessionId = pathParts[pathParts.length - 1];

  useEffect(() => {
    if (!sessionId || sessionId === 'mobile-pay') {
      setError('Invalid session ID');
      setLoading(false);
      return;
    }

    loadSession();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/checkout/${sessionId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Payment session not found');
        }
        if (response.status === 410) {
          throw new Error('This payment session has expired');
        }
        throw new Error('Failed to load payment details');
      }

      const data = await response.json();
      setSessionData(data);

      // Check if already paid
      if (data.status === 'PAID') {
        setConfirmed(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!sessionId) return;

    setConfirming(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/test/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to confirm payment');
      }

      setConfirmed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="mobile-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="mobile-container">
        <div className="mobile-card">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="mobile-container">
        <div className="mobile-card">
          <div className="success-message">
            <div className="success-icon"></div>
            <h2>Payment Successful!</h2>
            <p>Your payment has been confirmed.</p>
            {sessionData && (
              <div className="payment-details">
                <div className="amount-row">
                  <span className="amount-label">Amount Paid</span>
                  <span className="amount-value">
                    {formatCurrency(sessionData.amount, sessionData.currency)}
                  </span>
                </div>
              </div>
            )}
            <p>You can close this window and return to the merchant.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return null;
  }

  return (
    <div className="mobile-container">
      <div className="mobile-card">
        <div className="mobile-header">
          <h1>Complete Payment</h1>
          <p>via {sessionData.merchantName}</p>
        </div>

        <div className="dev-notice">
          <p>Development Mode: This is a mock payment confirmation</p>
        </div>

        <div className="payment-details">
          <div className="amount-row">
            <span className="amount-label">Total Amount</span>
            <span className="amount-value">
              {formatCurrency(sessionData.amount, sessionData.currency)}
            </span>
          </div>

          {sessionData.orderId && (
            <div className="detail-row">
              <span className="detail-label">Order ID</span>
              <span className="detail-value">{sessionData.orderId}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Merchant</span>
            <span className="detail-value">{sessionData.merchantName}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Expires</span>
            <span className="detail-value">{formatDate(sessionData.expiresAt)}</span>
          </div>
        </div>

        <div className="security-notice">
          <p>Secure payment processed by EstiaPay</p>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '16px' }}>
            <p>{error}</p>
          </div>
        )}

        <button
          className={`confirm-button ${confirming ? 'loading' : ''}`}
          onClick={handleConfirm}
          disabled={confirming}
        >
          {confirming ? (
            <>
              <span className="spinner" style={{ display: 'inline-block', width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}></span>
              Processing...
            </>
          ) : (
            `Confirm Payment ${formatCurrency(sessionData.amount, sessionData.currency)}`
          )}
        </button>

        <button className="cancel-button" onClick={() => window.close()}>
          Cancel
        </button>
      </div>

      <p style={{ textAlign: 'center', fontSize: '12px', color: '#999', marginTop: '20px' }}>
        Protected by EstiaPay secure payment gateway
      </p>
    </div>
  );
};

export default MobilePayPage;
